import type { OptionsDocument } from 'paintscout'

export interface PayrixCredentials {
  publicKey: string
  privateKey: string
  apiUrl: string
  portalUrl: string
  region: 'canada' | 'us'
}

function getCanadaVars(): PayrixCredentials {
  return {
    publicKey: process.env.PAYRIX_CANADA_PUBLIC_KEY || '',
    privateKey: process.env.PAYRIX_CANADA_PRIVATE_KEY || '',
    apiUrl: process.env.PAYRIX_CANADA_API_URL || '',
    portalUrl: process.env.PAYRIX_CANADA_PORTAL_URL || '',
    region: 'canada'
  }
}

function getUsVars(): PayrixCredentials {
  return {
    publicKey: process.env.PAYRIX_PUBLIC_KEY || '',
    privateKey: process.env.PAYRIX_PRIVATE_KEY || '',
    apiUrl: process.env.PAYRIX_API_URL || '',
    portalUrl: process.env.PAYRIX_PORTAL_URL || '',
    region: 'us'
  }
}

/**
 * Gets Payrix credentials based on the provided options.
 * Priority for determining country:
 * 1. options.options.integrations.payrix.config.country
 * 2. options.options.companyCountry.value
 * 3. Default to 'United States'
 */
export function getPayrixCredentials(options: OptionsDocument): PayrixCredentials {
  const payrixConfigCountry = getValidCountry(options.options.integrations?.payrix?.config?.country)
  const companyCountry = getValidCountry(options.options.companyCountry?.value)

  const effectiveCountry = payrixConfigCountry || companyCountry || 'United States'

  // Payrix is only available in Canada and United States
  // Use Canada credentials if explicitly set to Canada, otherwise use US credentials
  if (effectiveCountry === 'Canada') {
    return getCanadaVars()
  }

  // Default to US credentials for all other countries (United States, United Kingdom, New Zealand, Australia, undefined)
  return getUsVars()
}

function getValidCountry(value: unknown): string | null {
  return value && typeof value === 'string' && value.trim() ? value.trim() : null
}

/**
 * Gets Payrix credentials based on the provided region. Used in the api and gets region from a query parameter on the webhook.
 */
export function getPayrixCredentialsFromRegion(region: string): PayrixCredentials {
  if (region === 'canada') {
    return getCanadaVars()
  }

  return getUsVars()
}
